import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { useApi } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationRequest,
} from "@/shared/types";
import type { Bot } from "../model";
import type { CreateBotRequest } from "./api.dto";

export const useGetBotsByUserId = (
  params: PaginationRequest & { userId: string },
) => {
  const { api } = useApi();

  return useQuery<PaginatedResponse<Bot>>({
    queryKey: ["bots", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await api.get(`/bots/user/${params.userId}`, {
        params: filterObjectKeys(params, ["userId"]),
      });
      return response.data;
    },
    enabled: !!params.userId,
  });
};

export const useGetBotById = (id?: string, enabled: boolean = true) => {
  const { api } = useApi();

  return useQuery<ApiResponse<Bot>>({
    queryKey: ["bot", id],
    queryFn: async () => {
      const { data } = await api.get(`/bots/${id}`);
      return data;
    },
    enabled: !!id && enabled,
  });
};

export const useCreateBot = () => {
  const { api } = useApi();

  return useMutation<AxiosResponse, AxiosError, CreateBotRequest>({
    mutationFn: async (data) => {
      const formData = new FormData();
      for (const key in data) {
        const value = data[key as keyof CreateBotRequest];
        if (value !== undefined) {
          if (typeof value === "boolean") {
            formData.append(key, value.toString());
          } else if (value instanceof File) {
            formData.append(key, value);
          } else if (typeof value === "string") {
            formData.append(key, value);
          }
        }
      }
      const response = await api.post("/bots", formData);

      return response.data;
    },
  });
};
